<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, watchEffect } from "vue";
import AppIcon from "./components/AppIcon.vue";
import EndpointBoard from "./components/EndpointBoard.vue";
import SectionBlock from "./components/SectionBlock.vue";
import ServerSection from "./components/ServerSection.vue";
import TrafficDetail from "./components/TrafficDetail.vue";
import TrafficSection from "./components/TrafficSection.vue";
import WeatherHero from "./components/WeatherHero.vue";
import { useDashboard } from "./composables/useDashboard";
import { formatClock, stationTime } from "./format";
import { quotaCycle } from "./quota";
import {
  buildInsight,
  CONDITION_WORDS,
  deriveAdvisories,
  deriveWeather,
  describeAge,
  type Condition,
  type Weather
} from "./weather";

type Theme = "light" | "dark";
const THEME_KEY = "internet-weather-theme";
const THEME_COLORS: Record<Theme, string> = { light: "#f6f5f1", dark: "#101216" };
/** The API marks data stale at this age; mirror it for data that ages while refreshes fail. */
const STALE_SECONDS = 150;

const { data, loading, refreshing, error, refresh, refreshedAt, nextRefreshAt } = useDashboard();

// Theme: follows the system until the viewer picks one. index.html sets the first value
// before paint, so there is no flash of the wrong theme.
function savedTheme(): Theme | null {
  try {
    const saved = window.localStorage.getItem(THEME_KEY);
    return saved === "light" || saved === "dark" ? saved : null;
  } catch {
    return null;
  }
}
const systemDark = window.matchMedia("(prefers-color-scheme: dark)");
const theme = ref<Theme>(savedTheme() ?? (systemDark.matches ? "dark" : "light"));
function toggleTheme() {
  theme.value = theme.value === "light" ? "dark" : "light";
  try {
    window.localStorage.setItem(THEME_KEY, theme.value);
  } catch {
    // Private browsing: the choice simply lasts for this visit.
  }
}
function followSystem(event: MediaQueryListEvent) {
  if (!savedTheme()) theme.value = event.matches ? "dark" : "light";
}
watch(
  theme,
  (value) => {
    document.documentElement.dataset.theme = value;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLORS[value]);
  },
  { immediate: true }
);

// Two views share the page: the overview and the traffic detail behind #traffic.
const trafficOpen = ref(window.location.hash === "#traffic");
let overviewScroll = 0;
function syncRoute() {
  const next = window.location.hash === "#traffic";
  if (next === trafficOpen.value) return;
  if (next) overviewScroll = window.scrollY;
  trafficOpen.value = next;
  void nextTick(() => window.scrollTo({ top: next ? 0 : overviewScroll }));
}
function openTraffic() {
  window.location.hash = "traffic";
}
function closeTraffic() {
  window.location.hash = "overview";
}

// A slow clock for everything phrased relative to "now".
const now = ref(Date.now());
let clockTimer: number | undefined;
watch(refreshedAt, () => (now.value = Date.now()));

onMounted(() => {
  window.addEventListener("hashchange", syncRoute);
  systemDark.addEventListener("change", followSystem);
  clockTimer = window.setInterval(() => (now.value = Date.now()), 20_000);
});
onBeforeUnmount(() => {
  window.removeEventListener("hashchange", syncRoute);
  systemDark.removeEventListener("change", followSystem);
  if (clockTimer) window.clearInterval(clockTimer);
});

// Every endpoint is judged against its own last 24 hours, which the API summarises.
const insights = computed(() => (data.value?.endpoints ?? []).map(buildInsight));

/** Age of the sample on screen, counted on from the age the API reported when it was read. */
const ageSeconds = computed(() => {
  const age = data.value?.age_seconds;
  if (age == null) return null;
  return age + Math.max(0, (now.value - refreshedAt.value) / 1000);
});

const station = computed(() => stationTime(now.value, data.value?.timezone ?? "UTC"));
const quota = computed(() =>
  data.value?.bandwidth_quota ? quotaCycle(data.value.bandwidth_quota, now.value) : null
);
const observed = computed(() => data.value?.system != null && data.value.state !== "empty");
/** The traffic detail replaces the overview, once there is data to show in it. */
const detailOpen = computed(() => trafficOpen.value && observed.value);

const weather = computed<Weather>(() => {
  const night = station.value.hour < 6 || station.value.hour >= 18;
  const plain = (condition: Condition, sentence: string): Weather => ({
    condition,
    night,
    word: CONDITION_WORDS[condition],
    sentence,
    covers: []
  });
  const overview = data.value;
  if (!overview) {
    // No word yet: the hero keeps the space and the first real one fades in.
    if (loading.value) return { ...plain("unknown", "正在读取观测数据…"), word: "" };
    return plain("fog", `暂时读不到观测数据：${error.value || "API 没有返回有效响应"}。`);
  }
  const stale = ageSeconds.value != null && ageSeconds.value > STALE_SECONDS;
  return deriveWeather({
    state: stale && overview.state !== "empty" ? "stale" : overview.state,
    ageSeconds: ageSeconds.value,
    insights: insights.value,
    conntrackPercent: overview.system?.conntrack_percent ?? null,
    tableFullRecent: overview.conntrack_table_full_recent_events ?? 0,
    stationHour: station.value.hour
  });
});

const advisories = computed(() => {
  const overview = data.value;
  if (!overview) return [];
  const covered = weather.value.covers;
  return deriveAdvisories({
    refreshError: error.value,
    tableFullRecent: overview.conntrack_table_full_recent_events ?? 0,
    alertHoldSeconds: overview.alert_hold_seconds ?? 900,
    conntrackPercent: overview.system?.conntrack_percent ?? null,
    diskPercent: overview.system?.disk_percent ?? null,
    quota: quota.value,
    collectorError: overview.collector?.error ?? null
  }).filter((item) => !covered.includes(item.id));
});

const title = computed(() => data.value?.title || "Internet Weather");
/** Says little while samples arrive on time, and how late they are once they do not. */
const freshness = computed(() => {
  const age = ageSeconds.value;
  if (age == null) return "";
  return age < 120 ? "刚刚更新" : `${describeAge(age)}前更新`;
});
const observedAt = computed(() =>
  data.value?.timestamp ? `观测于 ${formatClock(data.value.timestamp * 1000)}` : ""
);
const reachable = computed(() => {
  const total = insights.value.length;
  const failing = insights.value.filter((item) => item.status === "failed" || item.status === "down").length;
  return failing ? `${total - failing} / ${total} 可达` : `${total} 个全部可达`;
});
const collectorNote = computed(() => {
  const duration = data.value?.collector?.duration_ms;
  if (duration == null) return "";
  return `上次采集耗时 ${duration >= 1000 ? `${(duration / 1000).toFixed(1)} s` : `${duration.toFixed(0)} ms`}`;
});

/** How long the ring around the refresh button takes to close: the wait for the next sample. */
const ringDuration = computed(() => `${Math.max(1, nextRefreshAt.value - refreshedAt.value)}ms`);

// The tab tells the weather too: the word in the title and a mark tinted by severity.
const MARK_COLORS: Record<Condition, string> = {
  clear: "#3478F6",
  cloudy: "#3478F6",
  showers: "#C27C00",
  rain: "#D03B3B",
  storm: "#D03B3B",
  fog: "#8C8B85",
  unknown: "#8C8B85"
};
watchEffect(() => {
  document.title = data.value ? `${weather.value.word} · ${title.value}` : title.value;
  const mark =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="16" fill="${MARK_COLORS[weather.value.condition]}"/>` +
    `<path d="M13 32h10l5-12 8 24 5-12h10" fill="none" stroke="#F7F9FE" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  document
    .querySelector('link[rel="icon"]')
    ?.setAttribute("href", `data:image/svg+xml,${encodeURIComponent(mark)}`);
});
</script>

<template>
  <div
    v-show="!detailOpen"
    class="sky"
    :class="[`sky--${weather.condition}`, { 'sky--night': weather.night }]"
    aria-hidden="true"
  ></div>
  <div class="page">
    <header class="topbar">
      <a class="brand" href="#overview" aria-label="回到概览">
        <img src="/monitor-mark.svg" alt="" width="22" height="22" />
        <span>{{ title }}</span>
      </a>
      <div class="topbar__actions">
        <span v-if="freshness" class="topbar__status" :title="observedAt">{{ freshness }}</span>
        <button
          class="icon-button refresh"
          type="button"
          :disabled="refreshing"
          aria-label="立即刷新"
          title="立即刷新"
          @click="refresh(true)"
        >
          <svg class="refresh__ring" viewBox="0 0 36 36" aria-hidden="true">
            <circle class="refresh__track" cx="18" cy="18" r="16.5" />
            <circle
              v-if="refreshedAt && !refreshing"
              :key="nextRefreshAt"
              class="refresh__progress"
              cx="18"
              cy="18"
              r="16.5"
              pathLength="100"
              :style="{ animationDuration: ringDuration }"
            />
          </svg>
          <AppIcon name="refresh" :size="16" :class="{ spinning: refreshing }" />
        </button>
        <button
          class="icon-button"
          type="button"
          :aria-label="theme === 'light' ? '切换到暗色主题' : '切换到亮色主题'"
          @click="toggleTheme"
        >
          <AppIcon v-if="theme === 'light'" name="moon" :size="17" />
          <AppIcon v-else name="sun" :size="17" />
        </button>
      </div>
    </header>

    <main>
      <TrafficDetail v-if="data && detailOpen" :data="data" :now="now" @close="closeTraffic" />

      <div v-show="!detailOpen">
        <WeatherHero
          :weather="weather"
          :advisories="advisories"
          :place="data?.location ?? ''"
          :clock="data ? station.label : ''"
        />
        <p v-if="!data && !loading" class="retry">
          <button class="solid-button" type="button" @click="refresh(true)">重新连接</button>
        </p>

        <template v-if="data && observed && data.system">
          <SectionBlock title="端点" :note="reachable">
            <EndpointBoard :endpoints="data.endpoints" :insights="insights" />
          </SectionBlock>
          <ServerSection :system="data.system" :table-full-recent="data.conntrack_table_full_recent_events ?? 0" />
          <TrafficSection :data="data" :now="now" @view-traffic="openTraffic" />
        </template>
      </div>
    </main>

    <footer class="footer">
      <p>HTTP 结果来自当前观测点，不能代表所有地区的访问质量。</p>
      <p class="footer__meta">
        <span v-if="collectorNote" class="num">{{ collectorNote }}</span>
        <a href="/api/docs">API 文档</a>
      </p>
    </footer>
  </div>
</template>

<style scoped>
/*
 * A faint glow in the colour of the weather, behind the hero's glyph. It lives in a layer
 * as wide as the viewport so it can spill past the page column without widening the page.
 */
.sky {
  --tint: transparent;
  position: absolute;
  inset: 0 0 auto;
  z-index: 0;
  height: 44rem;
  /* Centred on the glyph: right-aligned in the page column, just below the top bar. */
  background: radial-gradient(
    circle closest-side at
      calc(50% + min(50%, var(--page-width) / 2) - var(--gutter) - var(--glyph-size) / 2)
      calc(var(--topbar-height) + var(--hero-pad) + var(--glyph-size) / 2),
    color-mix(in srgb, var(--tint) var(--sky-strength, 24%), transparent),
    transparent
  );
  pointer-events: none;
}
.sky--clear { --tint: var(--sun); }
.sky--clear.sky--night { --tint: #6f7fe0; }
.sky--cloudy { --tint: #8da2b8; }
.sky--showers,
.sky--rain { --tint: var(--accent); }
.sky--storm { --tint: var(--bad); }
.sky--fog { --tint: #9a9a94; }

.refresh__ring { position: absolute; inset: 0; width: 100%; height: 100%; transform: rotate(-90deg); }
.refresh__ring circle { fill: none; stroke: var(--rule); stroke-width: 1.25; }
.refresh__ring .refresh__progress {
  stroke: var(--ink-3);
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  stroke-linecap: round;
}
.retry { margin: -1rem 0 4rem; }

/* The ring closes as the next sample approaches, then the page reads it. */
@media (prefers-reduced-motion: no-preference) {
  .refresh__ring .refresh__progress { animation: ring linear forwards; }
}
@keyframes ring { to { stroke-dashoffset: 0; } }
</style>
